/**
 * ui_event — a button press is the second currency (CLAUDE.md section 2), and until migration
 * 0019 every one of them vanished: Like was a local boolean, Share taps left no trace, the
 * chevron's use and the end card's reach were invisible.
 *
 * logUi() records one press: like, unlike, share, back (the chevron), end (the end card became
 * active), loop (BLITZ on the end card), link_open (the end card's "Link something"), and since
 * migration 0020 brief_copy ("Copy the brief") and make_open (the + sheet shown on Make one). It is
 * fire-and-forget: the RPC stamps who (from auth), what kind of client and which network (from
 * the request) and the visit's attribution (from the visit's own telemetry rows) — nothing here
 * is trusted beyond the kind, the card and the position. Every press is also kept in a local
 * log that the debug API exposes, so the e2e suite can read it with no backend.
 */
import { ensureSession, supabase } from '../backend/supabase';
import { visitId } from '../lib/visit';

export type UiKind = 'like' | 'unlike' | 'share' | 'back' | 'end' | 'loop' | 'link_open' | 'brief_copy' | 'make_open';

export interface UiLogEntry {
  kind: UiKind;
  card_id: string | null;
  visit_id: string;
  feed_position: number | null;
  /** epoch ms, when the press happened on this device */
  at: number;
}

const log: UiLogEntry[] = [];
let positionOf: () => number | undefined = () => undefined;

/** App wires this once so a press logged from a card knows where in the feed it sat. */
export function configureUi(opts: { position: () => number | undefined }): void {
  positionOf = opts.position;
}

/** Presses recorded on this device this page load, newest last (capped at 200). */
export function uiLog(): readonly UiLogEntry[] {
  return log;
}

export function logUi(kind: UiKind, cardId: string | null = null, feedPosition?: number): void {
  const entry: UiLogEntry = {
    kind,
    card_id: cardId,
    visit_id: visitId(),
    feed_position: feedPosition ?? positionOf() ?? null,
    at: Date.now(),
  };
  log.push(entry);
  if (log.length > 200) log.shift();
  if (!supabase) return;
  const client = supabase;
  void ensureSession()
    .then((session) => {
      if (!session) return;
      return client.rpc('log_ui', {
        p_kind: entry.kind,
        p_card_id: entry.card_id,
        p_visit_id: entry.visit_id,
        p_feed_position: entry.feed_position,
      });
    })
    .catch(() => {
      /* a lost press is a lost press; nothing here may block or retry into the bucket */
    });
}
